import { DateRangeFilter } from 'js/bulk_download/work_cards_shared/types';

const STORAGE_KEY = 'bulk_download.checkout_state.v1';
const MAX_WORK_IDS = 100; // server enforces 100 too
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface BulkDownloadCheckoutStateV1 {
  version: 1;
  storedAtMs: number;
  sourceWorkId: number;
  topicQuery: string;
  dateRange: DateRangeFilter;
  selectedBuckets: string[]; // does NOT include thePaperItself
  workIds: number[]; // ordered; SHOULD include source work
  workBuckets?: Record<number, string>; // workId → camelCase bucket name
}

function normalizeWorkIds(workIds: unknown): number[] {
  if (!Array.isArray(workIds)) return [];
  return workIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .map((id) => Math.trunc(id))
    .filter((id, idx, arr) => arr.indexOf(id) === idx)
    .slice(0, MAX_WORK_IDS);
}

export function storeBulkDownloadCheckoutState(
  state: Omit<BulkDownloadCheckoutStateV1, 'version' | 'storedAtMs'>,
): void {
  try {
    const payload: BulkDownloadCheckoutStateV1 = {
      version: 1,
      storedAtMs: Date.now(),
      sourceWorkId: Number(state.sourceWorkId),
      topicQuery: state.topicQuery ?? '',
      dateRange: state.dateRange ?? 'all',
      selectedBuckets: Array.isArray(state.selectedBuckets)
        ? state.selectedBuckets.filter(Boolean)
        : [],
      workIds: normalizeWorkIds(state.workIds),
      workBuckets: state.workBuckets ?? {},
    };

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // sessionStorage can throw (e.g., Safari private mode). Treat as best-effort.
    // eslint-disable-next-line no-console
    console.error('[bulk_download] failed to store checkout state', error);
  }
}

/**
 * Clear stored checkout state (e.g., on dismiss / "No thanks").
 * Prevents stale data from leaking into a later download.
 */
export function clearBulkDownloadCheckoutState(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    // ignore — sessionStorage may be unavailable
  }
}

export function consumeBulkDownloadCheckoutState(
  sourceWorkId: number,
): BulkDownloadCheckoutStateV1 | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    // Always clear: this state is meant to be single-use.
    window.sessionStorage.removeItem(STORAGE_KEY);

    const parsed = JSON.parse(raw) as Partial<BulkDownloadCheckoutStateV1>;
    if (parsed.version !== 1) return null;
    if (typeof parsed.storedAtMs !== 'number') return null;
    if (Date.now() - parsed.storedAtMs > TTL_MS) return null;
    if (Number(parsed.sourceWorkId) !== Number(sourceWorkId)) return null;

    return {
      version: 1,
      storedAtMs: parsed.storedAtMs,
      sourceWorkId: Number(parsed.sourceWorkId),
      topicQuery:
        typeof parsed.topicQuery === 'string' ? parsed.topicQuery : '',
      dateRange: (parsed.dateRange ?? 'all') as DateRangeFilter,
      selectedBuckets: Array.isArray(parsed.selectedBuckets)
        ? parsed.selectedBuckets.filter(Boolean).map(String)
        : [],
      workIds: normalizeWorkIds(parsed.workIds),
      workBuckets:
        parsed.workBuckets && typeof parsed.workBuckets === 'object'
          ? (parsed.workBuckets as Record<number, string>)
          : {},
    };
  } catch (error) {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      // ignore
    }
    // eslint-disable-next-line no-console
    console.error('[bulk_download] failed to consume checkout state', error);
    return null;
  }
}
